import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/auth/AuthContext';
import { supabase } from '../../lib/supabaseClient';
import TimeClock from '../../components/crew/TimeClock';
import TimeLogHistory from '../../components/crew/TimeLogHistory';
import MyServices from '../../components/crew/MyServices';

interface Appointment {
  id: string;
  start_time: string;
  end_time: string;
  status: string;
  client_id: string;
  notes: string | null;
}

interface AppointmentWithClient extends Appointment {
  client_name: string;
  client_email: string;
  services: string[];
}

const CrewDashboard: React.FC = () => {
  const { userProfile } = useAuth();
  const [upcomingAppointments, setUpcomingAppointments] = useState<AppointmentWithClient[]>([]);
  const [stats, setStats] = useState({
    todayCount: 0,
    weekCount: 0,
    totalCompleted: 0
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchDashboardData = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Find the team member record for this user
      const { data: teamMember, error: teamError } = await supabase
        .from('team')
        .select('id')
        .eq('user_id', userProfile?.id)
        .single();

      if (teamError || !teamMember) {
        throw new Error('Team member profile not found');
      }

      const staffId = teamMember.id;

      // Get upcoming appointments (next 7 days)
      const now = new Date();
      const weekFromNow = new Date();
      weekFromNow.setDate(weekFromNow.getDate() + 7);

      const { data: appointmentsData, error: appointmentsError } = await supabase
        .from('appointments')
        .select(`
          id,
          start_time,
          end_time,
          status,
          client_id,
          notes,
          user_profiles (
            full_name,
            email
          ),
          appointment_services (
            services (
              name
            )
          )
        `)
        .eq('staff_id', staffId)
        .gte('start_time', now.toISOString())
        .lte('start_time', weekFromNow.toISOString())
        .order('start_time', { ascending: true })
        .limit(10);

      if (appointmentsError) {
        throw appointmentsError;
      }

      // Transform data
      const transformed = (appointmentsData || []).map((apt: any) => ({
        id: apt.id,
        start_time: apt.start_time,
        end_time: apt.end_time,
        status: apt.status,
        client_id: apt.client_id,
        notes: apt.notes,
        client_name: apt.user_profiles?.full_name || 'Guest',
        client_email: apt.user_profiles?.email || '',
        services: apt.appointment_services?.map((as: any) => as.services?.name).filter(Boolean) || []
      }));

      setUpcomingAppointments(transformed);

      // Calculate stats
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);

      const todayAppts = transformed.filter((apt: AppointmentWithClient) => {
        const aptDate = new Date(apt.start_time);
        return aptDate >= today && aptDate < tomorrow;
      });

      const { count: completedCount } = await supabase
        .from('appointments')
        .select('*', { count: 'exact', head: true })
        .eq('staff_id', staffId)
        .eq('status', 'completed');

      setStats({
        todayCount: todayAppts.length,
        weekCount: transformed.length,
        totalCompleted: completedCount || 0
      });

    } catch (err: any) {
      setError(err.message || 'Failed to load dashboard data');
    } finally {
      setIsLoading(false);
    }
  }, [userProfile]);

  useEffect(() => {
    if (userProfile) {
      fetchDashboardData();
    }
  }, [userProfile, fetchDashboardData]);

  // Setup realtime subscription for appointment changes
  useEffect(() => {
    if (!userProfile?.id) return;

    const setupRealtimeSubscription = async () => {
      // Get team member ID first
      const { data: teamMember } = await supabase
        .from('team')
        .select('id')
        .eq('user_id', userProfile.id)
        .single();

      if (!teamMember) return;

      const channel = supabase
        .channel('crew-appointments-changes')
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'appointments',
            filter: `staff_id=eq.${teamMember.id}`
          },
          (payload) => {
            // Refresh dashboard data when any change occurs
            fetchDashboardData();
          }
        )
        .subscribe();

      // Cleanup subscription on unmount
      return () => {
        supabase.removeChannel(channel);
      };
    };

    const cleanup = setupRealtimeSubscription();
    return () => {
      cleanup.then(cleanupFn => cleanupFn && cleanupFn());
    };
  }, [userProfile, fetchDashboardData]);

  const formatTime = (dateString: string) => {
    // Add 'Z' if missing to parse as UTC (matches admin approach)
    const utcDateString = dateString.endsWith('Z') ? dateString : dateString + 'Z';
    const date = new Date(utcDateString);
    return date.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit', 
      hour12: true,
      timeZone: 'America/Chicago' 
    });
  };

  const formatDate = (dateString: string) => {
    // Add 'Z' if missing to parse as UTC (matches admin approach)
    const utcDateString = dateString.endsWith('Z') ? dateString : dateString + 'Z';
    const date = new Date(utcDateString);
    return date.toLocaleDateString('en-US', { 
      weekday: 'short', 
      month: 'short', 
      day: 'numeric',
      timeZone: 'America/Chicago'
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="inline-block w-12 h-12 border-4 border-[#B2D8C5] border-t-[#3A5E63] rounded-full animate-spin mb-4"></div>
          <p className="text-[#344854]">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4">
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Welcome section */}
      <div className="bg-white rounded-lg shadow p-6">
        <h1 className="text-2xl font-bold text-[#344854] mb-2">
          Welcome, {userProfile?.full_name || 'Crew Member'}!
        </h1>
        <p className="text-gray-600">Here's your schedule overview</p>
      </div>

      {/* Stats cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Today's Appointments</p>
              <p className="text-3xl font-bold text-[#3A5E63] mt-2">{stats.todayCount}</p>
            </div>
            <div className="text-4xl">📅</div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">This Week</p>
              <p className="text-3xl font-bold text-[#3A5E63] mt-2">{stats.weekCount}</p>
            </div>
            <div className="text-4xl">📊</div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Completed Total</p>
              <p className="text-3xl font-bold text-[#3A5E63] mt-2">{stats.totalCompleted}</p>
            </div>
            <div className="text-4xl">✅</div>
          </div>
        </div>
      </div>

      {/* Time Clock and History */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <TimeClock />
        <TimeLogHistory />
      </div>

      {/* My Services (Paid appointments with tips) */}
      <MyServices />

      {/* Upcoming appointments */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b">
          <h2 className="text-xl font-semibold text-[#344854]">Upcoming Appointments</h2>
        </div>
        <div className="p-6">
          {upcomingAppointments.length === 0 ? (
            <p className="text-gray-500 text-center py-8">No upcoming appointments</p>
          ) : (
            <div className="space-y-4">
              {upcomingAppointments.map((appointment) => (
                <div
                  key={appointment.id}
                  className="border border-gray-200 rounded-lg p-4 hover:border-[#3A5E63] transition-colors"
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold text-[#344854]">{appointment.client_name}</h3>
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          appointment.status === 'scheduled' ? 'bg-green-100 text-green-800' :
                          appointment.status === 'pending_payment' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {appointment.status}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 mb-2">
                        {formatDate(appointment.start_time)} • {formatTime(appointment.start_time)} - {formatTime(appointment.end_time)}
                      </p>
                      {appointment.services.length > 0 && (
                        <p className="text-sm text-gray-500">
                          Services: {appointment.services.join(', ')}
                        </p>
                      )}
                      {appointment.notes && (
                        <p className="text-sm text-gray-500 mt-1 italic">
                          Note: {appointment.notes}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CrewDashboard;
